/* eslint-disable max-lines */
/* eslint-disable dot-notation */

import { action, intercept, observable } from 'mobx';
import { AnalyticsStore, Store } from 'bernie-plugin-mobx';

import { getErrorString, Logger, NOOP_LOGGER } from 'bernie-logger';
import { Localization } from 'bernie-l10n';
import { SerializedData } from 'bernie-core';

import { ILocalization } from '@shared-ui/localization-context';

import { BexApiSource } from 'sources/bex-api-source';

import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { STORE_EVENTS } from 'stores/events/store-events';

import { DefaultRequestFields } from 'models';

import isPayload, { Payload } from './adobe-payload';

export interface OfferSearchData {
  startDate?: string;
  endDate?: string;
  adults?: string;
  children?: string;
  petsIncluded?: boolean;
}

interface CurrentHotelStoreDefaults {
  hotelId?: number;
  isNavigationFromSRP: boolean;
  l10n: ILocalization;
  offerSearchData: OfferSearchData;
  reviewIdsUpvoted: string[];
  poiData: Record<string, unknown>;
  selectedGalleryCategoryId: string;
}

export class CurrentHotelStore extends Store {
  constructor(private bexApiSource: BexApiSource, state: SerializedData = {}, l10n: ILocalization, logger: Logger = NOOP_LOGGER) {
    super(state, logger);

    this.l10n = l10n;

    this.searchCriteria = new SearchCriteriaModel({}, this.l10n);
    this.numberInterceptor('hotelId');
  }

  public hydrate(data: SerializedData): void {
    if (data && data.hotel && data.hotel.code === 'INACTIVE_HOTEL_ID') {
      window.location.replace(`https://${window.location.host}/Hotels`);
    }

    delete data.bexApiSource;
    delete data.hotelSearchApiSource;

    this.searchCriteria.hydrate(data.searchCriteria || {});
    delete data.searchCriteria;

    Object.assign(this, data);
  }

  // setup mobx interceptor to wrap dates in a method to format data passed to setter
  public numberInterceptor(prop: any): void {
    if (typeof prop === 'string') {
      // Ignoring interceptors for now since they are going to be refactored out in the future
      // @ts-ignore
      intercept(this, prop as any, change => {
        if (change.newValue) {
          const newValue = this.sanitizeNumber(change.newValue);

          if (newValue) {
            change.newValue = newValue;

            return change;
          }
        }
      });
    }
  }

  public sanitizeNumber(number: any): number | string {
    try {
      const cleanNumber = typeof number === 'number' ? number : parseInt(number, 10);

      if (Number.isNaN(cleanNumber)) {
        throw new Error(`Invalid Number: cannot parse ${number} into a Number`);
      }

      return cleanNumber;
    } catch (error) {
      this.logErrorEvent(STORE_EVENTS.SANATIZE_NUMNBER_ERROR, getErrorString(error as Error));

      return '';
    }
  }

  public static offerSearchKeys = ['startDate', 'endDate', 'adults', 'children', 'petsIncluded'];

  public static DEFAULTS: CurrentHotelStoreDefaults = {
    hotelId: undefined,
    isNavigationFromSRP: false,
    l10n: new Localization('en_US'),
    offerSearchData: {},
    reviewIdsUpvoted: [],
    poiData: {},
    selectedGalleryCategoryId: 'all',
  };

  @observable
  public l10n: ILocalization;
  @observable
  public hotelId!: number;
  @observable
  public isNavigationFromSRP = false;
  @observable
  public isLoading!: boolean;
  @observable
  public isFailure!: boolean;
  @observable
  public shouldFetch!: boolean;
  @observable
  public openNewDatePicker = false;
  @observable
  public loadingETPData!: boolean;
  @observable
  public creatingConversation!: boolean;
  @observable
  public offerSearchData!: OfferSearchData;

  @observable
  public travelAd: any;
  @observable
  public requests: any;

  @observable.ref
  public searchCriteria: SearchCriteriaModel;

  @observable
  public poiData: Record<string, unknown> = CurrentHotelStore.DEFAULTS.poiData;

  private _fetchingHotel!: number;

  public selectedMapMarker!: number;

  public offerExtensions: any;

  public defaultRequestFields!: DefaultRequestFields;

  public apolloLinkSetOnBrowser = false;
  public apolloLinkSetOnServer = false;

  private detailsPayload!: Payload | null;
  private offersPayload!: Payload | null;

  @observable
  public showPetsOnSingleOffer = false;

  public hotel: any;

  private resetPayloads() {
    this.detailsPayload = null;
    this.offersPayload = null;
  }

  public addPayload(operation: string, payload: Record<string, unknown>, analytics: AnalyticsStore): void {
    if (isPayload(payload)) {
      if (operation === 'PropertyDetailsQuery') {
        this.detailsPayload = payload;
      }
      if (operation === 'PropertyOffersQuery') {
        this.offersPayload = payload;
      }

      if (this.offersPayload && this.detailsPayload) {
        analytics.publishClientSidePayload([this.detailsPayload, this.formatOmnitureData(this.offersPayload)]);
        this.resetPayloads();
      }
    }
  }

  @action
  public setIsNavigationFromSRP(isNavigationFromSRP: boolean) {
    this.isNavigationFromSRP = isNavigationFromSRP;
  }

  @action
  public setPropertyListingAsCurrentHotel(lodgingCard?: { id: string }): void {
    if (!lodgingCard) {
      return;
    }

    const { id } = lodgingCard;

    const propertyId = parseInt(id, 10);

    this.setIsNavigationFromSRP(true);

    if (this.hotelId === propertyId) {
      return;
    }

    this.shouldFetch = true;

    this.hotelId = propertyId;
  }

  @action
  public setPropertyListingIdAsCurrentHotel(id: string): void {
    if (!id) {
      return;
    }

    const propertyId = parseInt(id, 10);

    if (this.hotelId === propertyId) {
      return;
    }

    this.shouldFetch = true;

    this.hotelId = propertyId;
  }

  @action
  public updateSearchCriteria(criteria: SearchCriteriaModel): void {
    this.searchCriteria = criteria;
  }

  @action
  public setHotelId(hotelId: number) {
    const expediaPropertyIdParam = this.searchCriteria.expediaPropertyId;
    if (hotelId !== this.hotelId) {
      this.shouldFetch = true;
      this.hotelId = parseInt(expediaPropertyIdParam ?? '', 10) || hotelId;
    }
  }

  private formatOmnitureData = (omnitureData: Payload) => {
    const firstAttribute = omnitureData.adobeMappedData?.products?.split('=')?.find(Boolean);
    const hotelString = `;Hotel:${this.hotelId};;;`;
    omnitureData.adobeMappedData.products = `${hotelString}${omnitureData.adobeMappedData.products}`;
    if (firstAttribute) {
      omnitureData.adobeMappedData.products = omnitureData.adobeMappedData.products.replace(
        new RegExp(`&${firstAttribute}`, 'g'),
        `,${hotelString}${firstAttribute}`
      );
    }

    return omnitureData;
  };

  private logErrorEvent(name: any, err: any) {
    this.logger.logEvent(name, getErrorString(err));
  }

  @action
  public setOfferData(offerData: any): void {
    this.offerSearchData = Object.assign({}, this.offerSearchData, offerData);
  }

  @action
  public clearPoiData() {
    this.poiData = CurrentHotelStore.DEFAULTS.poiData;
  }

  public setSelectedMapMarker(markerId: number) {
    this.selectedMapMarker = markerId ? Number(markerId) : markerId;
  }
}
